import {Component, OnInit, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {DistrictBuildingDto} from '../../models/district/districtBuildingDto';
import {TokenService} from '../../services/token.service';
import {StudentDto} from '../../common/dtos/studentDto';
import {Role} from '../../models/district/role.model';
import {DistrictService} from '../../services/district.service';
import {StaffService} from '../../services/staff.service';
import {IDirty} from '../../services/canDeactivate.service';
import {Level, ToastService} from '../../services/toast.service';
import {LoggingService} from '../../services/logging';
import {StaffDto, RoleDto} from '@sidr/dto-types';

@Component({
  selector: 'app-staff-view',
  templateUrl: './staffPage.component.html'
})
export class StaffPageComponent implements OnInit, IDirty {
  title = 'Staff Edit';

  edit: boolean;
  canEdit: boolean;

  canAdmin: boolean;

  staffId: string;
  staff: StaffDto;
  students: StudentDto[];

  school_id: string;
  buildings: DistrictBuildingDto[];
  roles: RoleDto[];

  original: StaffDto;

  user_roles: Role;

  @ViewChild('staffForm') staffForm: NgForm;

  constructor(private router: ActivatedRoute,
              private staffService: StaffService,
              private districtService: DistrictService,
              private tokenService: TokenService,
              private toastService: ToastService,
              private log: LoggingService) {
    this.edit = false;
    this.canEdit = false;
    this.canAdmin = false;
  }

  editStaff() {
    this.original = {...this.staff}
    console.log(this.original);
    this.edit = true;
    this.canAdmin = (this.tokenService.getUserToken()?.roles.indexOf('can_admin') ?? -1) > -1;
  }

  cancel() {
    console.log(this.original);
    this.staff = {...this.original};
    this.staffForm.form.markAsPristine();
    this.edit = false;
  }


  hasBuilding(id: string): boolean {
    return this.staff.buildings.indexOf(id) > -1;
  }

  toggleBuilding(id: string) {
    const index = this.staff.buildings.indexOf(id);
    this.log.Log(`Index of building ${index}`);
    if (index > -1) {
      this.log.Log(`Removed building ${id}`);
      this.staff.buildings.splice(index, 1);
    } else {
      this.log.Log(`Adding building ${id}`);
      this.staff.buildings.push(id);
    }
    this.staffForm.form.markAsDirty();
  }

  hasRole(id: string): boolean {
    return (this.staff.roleIds?.indexOf(id) ?? -1) > -1;
  }

  impersonate() {
    this.staffService.impersonate(this.staffId).subscribe(x => {
      this.tokenService.setToken(x.token);
      this.tokenService.setRefresh(x.refresh);
      window.location.reload();
    });
  }

  toggleEnableDisabled() {
    this.log.Log(`Setting disabled from ${this.staff.disabled} to ${!this.staff.disabled}`);
    this.staff.disabled = !this.staff.disabled;
    this.staffForm.form.markAsDirty();
  }

  toggleRole(id: string) {
    this.staff.roleIds = this.staff.roleIds || [];
    const index = this.staff.roleIds.indexOf(id);
    if (index > -1) {
      this.staff.roleIds.splice(index, 1);
    } else {
      this.staff.roleIds.push(id);
    }
    this.staffForm.form.markAsDirty();
  }

  ngOnInit(): void {
    this.router.params.subscribe(p => {
      this.staffId = p['staff_id'];
      this.edit = false;
      this.staffService.get(this.staffId, true, true).subscribe(r => {
        this.staff = r;
        this.staff.buildings = this.staff.buildings || [];
        this.staff.roleIds = this.staff.roleIds || [];
        this.staff.students = this.staff.students || [];
        this.students = [];

        this.districtService.getBuildings(r.districtId).subscribe(b => {
          this.buildings = b;
        }, err => {
          this.toastService.AddNotification(Level.ERROR, this.title, 'Failed to retrieve buildings');
        });
        this.districtService.getRoles(r.districtId).subscribe(roles => {
          this.roles = roles;
        }, err => {
          this.toastService.AddNotification(Level.ERROR, this.title, 'Failed to retrieve roles');
        });
      }, err => {
        this.toastService.AddNotification(Level.ERROR, this.title, 'Failed to retrieve staff member');
      });
      this.staffService.getStudent(this.staffId).subscribe(r => {
        this.students = r;
      }, err => {
        this.toastService.AddNotification(Level.ERROR, this.title, 'Failed to retrieve students');
      });
      const me = this.tokenService.getUserToken();

      const roles = this.tokenService.getUserToken()?.roles ?? [];
      this.canEdit = roles.indexOf('can_admin') > -1 && roles.indexOf('modify_staff') > -1;
    });
  }

  reload() {
    this.edit = false;
    this.staffService.get(this.staffId, true, true).subscribe(r => {
      this.staff = r;
    }, err => {
      this.toastService.AddNotification(Level.ERROR, this.title, 'Failed to retrieve staff member');
    });
    this.staffService.getStudent(this.staffId).subscribe(r => {
      this.students = r;
    }, err => {
      this.toastService.AddNotification(Level.ERROR, this.title, 'Failed to retrieve students');
    });

  }

  addStudent(school_id: string) {
    this.districtService.getStudentBySchoolId(this.staff.districtId, school_id).subscribe(resp => {
      this.staff.students = this.staff.students || [];
      if (this.staff.students.indexOf(resp.id) > -1) {
        this.toastService.AddNotification(Level.ERROR, this.title, 'Student already added');
        this.school_id = '';
        return;
      }
      this.students.push(resp);
      this.staff.students.push(resp.id);
      this.school_id = '';
    }, () => {
      this.toastService.AddNotification(Level.ERROR, this.title, 'Failed to retrieve student information');
    });
  }

  removeStudent(id: string) {
    const index = this.staff?.students?.indexOf(id) ?? -1;
    if (index > -1 && this.staff?.students) {
      this.staff.students.splice(index, 1);
    }

    this.staffForm.form.markAsDirty();
  }

  save() {
    this.staff.students?.filter((v,i) => this.staff.students?.indexOf(v) == i);
    this.staffService.patch(this.staff).subscribe(r => {
      this.staffForm.form.reset();
      this.staffForm.form.markAsPristine();
      this.reload();
      this.toastService.AddNotification(Level.SUCCESS, this.title, 'Successfully saved');
    }, err => {
      this.toastService.AddNotification(Level.ERROR, this.title, 'Failed to save');
    });
  }

  isDirty(): boolean {
    return this.staffForm.form.dirty;
  }
}
